import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiException, ProblemDetailsDto, ValidationProblemDetailsDto } from '../models';

/**
 * HTTP client wrapper for API communication.
 * CPNotes: Handles API calls with error mapping and base URL configuration
 */
@Injectable({
  providedIn: 'root'
})
export class ApiClientService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiBaseUrl;

  /**
   * Performs a GET request.
   */
  get<T>(endpoint: string): Observable<T> {
    return this.http.get<T>(`${this.baseUrl}/${endpoint}`).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Performs a POST request.
   */
  post<T>(endpoint: string, data?: any): Observable<T> {
    return this.http.post<T>(`${this.baseUrl}/${endpoint}`, data || {}).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Performs a PUT request.
   */
  put<T>(endpoint: string, data: any): Observable<T> {
    return this.http.put<T>(`${this.baseUrl}/${endpoint}`, data).pipe(
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Performs a DELETE request.
   */
  delete(endpoint: string): Observable<boolean> {
    return this.http.delete(`${this.baseUrl}/${endpoint}`).pipe(
      map(() => true),
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Handles HTTP errors and converts them to ApiException.
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let apiException: ApiException;

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      apiException = {
        statusCode: 0,
        message: error.error.message
      };
    } else {
      // Server-side error
      const body = error.error;

      // Try to parse as ValidationProblemDetails
      if (body?.errors && typeof body.errors === 'object') {
        const validationError = body as ValidationProblemDetailsDto;
        const errorMessages = Object.values(validationError.errors || {})
          .flat()
          .filter((msg: any) => msg && typeof msg === 'string');
        const combinedMessage = errorMessages.join(', ');

        apiException = {
          statusCode: error.status,
          title: validationError.title,
          detail: combinedMessage,
          message: combinedMessage || validationError.detail || error.message
        };
      }
      // Try to parse as ProblemDetails
      else if (body?.detail || body?.title) {
        const problem = body as ProblemDetailsDto;
        apiException = {
          statusCode: error.status,
          title: problem.title,
          detail: problem.detail,
          message: problem.detail || problem.title || error.message
        };
      }
      // Try simple error message
      else if (body?.message) {
        apiException = {
          statusCode: error.status,
          message: body.message
        };
      }
      // Fallback
      else {
        apiException = {
          statusCode: error.status,
          message: error.message || 'An error occurred'
        };
      }
    }

    return throwError(() => apiException);
  }
}

